import * as _4 from 'lodash4';
import { ICalendarEvent, ICalendarLocalDatabase, IScheduleCourses } from './../types/my_schedule_types';

export interface IMyScheduleNativeCalenderFunction {
  selectCalendarToAddCourses: (isResync: boolean) => ng.IPromise<any>;
  addSingleCourseToCalendar: (section: any) => ng.IPromise<string>;
  addAllCoursesToCalendar: (sectionList: any) => ng.IPromise<string>;
  deleteSingleCourseToCalendar: (courseEvent: any) => ng.IPromise<string>;
  deleteAllCoursesFromCalendar: (courseEvents: any) => ng.IPromise<string>;
  createCourseCalendarObjects: (listOfCourses: IScheduleCourses[]) => ICalendarEvent[];
}

interface ICalendar {
  id: string;
  name: string;
}

export function MyScheduleNativeCalenderFunction(
  calendarFacade: any,
  $q: any,
  $timeout: any,
  ngCalendarLocalDatabase: ICalendarLocalDatabase
) {
  class MyScheduleNativeCalendar {
    calendar: ICalendar = {} as ICalendar;
    selectCalendarToAddCourses(isResync: boolean = false): ng.IPromise<any> {
      const deferred = $q.defer();

      calendarFacade.selectCalendar((cal: ICalendar) => {
        if (!_4.isUndefined(cal)) {
          this.calendar = cal;
          deferred.resolve(cal);
        } else {
          deferred.reject();
        }
      }, isResync);

      return deferred.promise;
    }

    addSingleCourseToCalendar(section: any) {
      /*
       * this is a promise to allow for the plugin to finish before moving on
       * to the next object and to--once again--work around the plugin's lack
       * of multi-threading support
       * */
      const deferred = $q.defer();
      const successFn: (err: any) => void = (evt: any) => {
        // add native calendar event ID to object
        section.nativeCalendarEventId = evt;
        ngCalendarLocalDatabase.addCourse(section, this.calendar, evt);
        deferred.resolve(evt);
      };

      const errFn: (err: any) => void = (err: any) => {
        deferred.reject('error adding event to calendar', err);
      };

      calendarFacade.registerEventWithCallbacks(section, successFn, errFn);
      return deferred.promise;
    }

    addAllCoursesToCalendar(sectionList: any) {
      /*
       * We have to recursively call this function to allow for us to iterate over
       * the list of courses. Again--the plugin is single threaded. The completionFn
       * serves as a way to kind of handle the resolution of all of this.
       * */
      const deferred = $q.defer();
      let iterator: number = 0;
      const addSingleCourse: (section: any) => void = (section: any) => {
        this.addSingleCourseToCalendar(section).then((evt: any) => {
          // incrememnt our counter
          iterator += 1;
          if (iterator < sectionList.length) {
            // timeout is another safegaurd from the single-threading, then recurse
            /* tslint:disable */
            $timeout(addSingleCourse(sectionList[iterator]), 0);
            /* tslint:enable */
          } else {
            // fire the completion event if there is one
            deferred.resolve();
          }
        });
      };

      // initialize the loop/recursion
      addSingleCourse(sectionList[iterator]);
      return deferred.promise;
    }
    createCourseCalendarObjects(listOfCourses: any[]): ICalendarEvent[] {
      /*
       * This function preprocesses the objects before we feed them to the plugin.
       * We need to do this because all of the logic below has been known to prevent
       * some date instances from being added to the calendar. This fn returns
       * an array of objects formatted FOR THE CALENDAR ONLY which means that they are
       * broken out into objects based on the sectionTime property of the master list
       * and are not based on the individual courses.
       *
       * Also, makes testing easier!
       * */

      const calendarObjects: any[] = [];

      _4.forEach(listOfCourses, (section: any) => {
        /*
         * before the loop, we set the information of the parent course
         * to local variables that we will need to reference in the loop.
         * Also all time needs to be a raw JS date object for the plugin.
         * */

        const eventObjConfig = {
          title: section._course.title,
          notes: section.note,
          nk: section.nk
        };
        const eventOption = {
          recurrence: 'weekly',
          recurrenceEndDate: section.class_end_dt.toDate(),
          recurrenceInterval: 1,
          calendarId: this.calendar.id,
          calendarName: this.calendar.name
        };
        const courseStartingObj = section.class_start_dt;

        _4.forEach(section._sectionTime, (sectionTime: any) => {
          let calculatedWeekday;
          let baseStart;
          let baseEnd;
          let sectionStartingTime;
          let sectionEndingTime;
          let sectionEvent: any;

          baseStart = moment(sectionTime.from_tm, 'HH:mm:ss').toObject();
          baseEnd = moment(sectionTime.to_tm, 'HH:mm:ss').toObject();

          /*
           * we subtract 1 because server days [1-7] are +1 compared to moment days [0-6]
           * we also check for a day greater than 7 which means virtual/no temporal dates.
           * */
          if (sectionTime.day_of_week > 7) {
            return;
          } else if (sectionTime.day_of_week - 1 < courseStartingObj.day()) {
            calculatedWeekday = sectionTime.day_of_week + 6;
          } else {
            calculatedWeekday = sectionTime.day_of_week - 1;
          }

          /*
           * we have to clone the course start date to give the calendar
           * a day to start the recurrence on, otherwise the calendar instance
           * begins today. The funkiness afterwards is to alter the copy with the time
           * of the recurring class.
           * */
          sectionStartingTime = courseStartingObj
            .clone()
            .day(calculatedWeekday)
            .hour(baseStart.hours)
            .minute(baseStart.minutes);
          sectionEndingTime = courseStartingObj
            .clone()
            .day(calculatedWeekday)
            .hour(baseEnd.hours)
            .minute(baseEnd.minutes);

          // all time needs to be a raw JS date object for the plugin
          sectionEvent = _4.merge({}, eventObjConfig, {
            location: _4.get(sectionTime, '_location.name', 'n/a'),
            startDate: sectionStartingTime.toDate(),
            endDate: sectionEndingTime.toDate(),
            timeId: sectionTime.id
          });

          sectionEvent.options = eventOption;
          calendarObjects.push(sectionEvent);
        });
      });
      return calendarObjects;
    }

    deleteSingleCourseFromCalendar(courseEvent: any) {
      const deferred = $q.defer();
      const successFn: (err: any) => void = () => {
        ngCalendarLocalDatabase.removeCourse(courseEvent);
        deferred.resolve();
      };
      const errFn: (err: any) => void = (err: any) => {
        deferred.reject('Error removing event from calendar', err);
      };
      calendarFacade.deleteEventWithCallbacks(courseEvent.calendarEventID, successFn, errFn);
      return deferred.promise;
    }

    deleteAllCoursesFromCalendar(courseEvents: any) {
      /* We have to recursively call this function to allow for us to iterate over the list of courses. Again--the plugin is single threaded. The completionFn serves as a way to kind of handle the resolution of all of this.*/
      const deferred = $q.defer();
      let iterator: number = 0;
      const deleteSingleCourse = (courseEvent: any) => {
        // If no courseEvent, fire the completion event
        if (_4.isEmpty(courseEvent)) {
          deferred.resolve();
          return;
        }
        this.deleteSingleCourseFromCalendar(courseEvent).then((evt: any) => {
          iterator += 1;
          if (iterator < courseEvents.length) {
            // timeout is another safegaurd from the single-threading, then recurse
            /* tslint:disable */
            $timeout(deleteSingleCourse(courseEvents[iterator]), 0);
            /* tslint:enable */
          } else {
            // fire the completion event if there is one
            deferred.resolve();
          }
        });
      };
      // initialize the loop/recursion
      deleteSingleCourse(courseEvents[iterator]);
      return deferred.promise;
    }
  }
  return new MyScheduleNativeCalendar();
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-course-schedule/src/functions/my_schedule_native_calendar_fn.ts